import { React, useEffect, useState } from "react";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Box
} from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import InviteCodeForm from "./Forms/InviteCodeForm";
import { api } from "../../utils/api";
import { useNavigate } from "react-router-dom";

const InviteCode = () => {
  const navigate = useNavigate();
  const [inviteCodeOption, setInviteCodeOption] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteCodeForm, setShowInviteCodeForm] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchInviteCodes();
  }, []);

  const fetchInviteCodes = async () => {
    try {
      const response = await api("v1/inviteCode", "GET");
      if (response.data.success) {
        const inviteCodeOptions = response.data.data.allInviteCodes;

        // Customer data should now be populated from backend
        // But we'll still handle cases where it might not be populated
        const updatedInviteCodeOptions = inviteCodeOptions.map((inviteCodeOption) => {
          // If customer is already populated (object with firstName, lastName, etc), use it
          if (
            inviteCodeOption.customer &&
            typeof inviteCodeOption.customer === "object" &&
            inviteCodeOption.customer.firstName
          ) {
            return inviteCodeOption;
          }

          // If customer is just an ID, we'll need to fetch it (fallback)
          const customerId = inviteCodeOption.customer?._id || inviteCodeOption.customer;
          if (customerId && typeof customerId === "string") {
            // Return as-is, we'll fetch customer data if needed
            return {
              ...inviteCodeOption,
              customer: null, // Will be fetched below if needed
              _customerId: customerId // Store ID for fetching
            };
          }

          return {
            ...inviteCodeOption,
            customer: null
          };
        });

        // Only fetch customer data for invite codes that don't have it populated
        const needsFetching = updatedInviteCodeOptions.filter((ic) => ic._customerId && !ic.customer);

        if (needsFetching.length > 0) {
          await Promise.all(
            needsFetching.map(async (inviteCodeOption) => {
              try {
                const customerResponse = await api(`v1/customer/${inviteCodeOption._customerId}`, "GET");
                if (customerResponse.data.success && customerResponse.data.data.customer) {
                  const index = updatedInviteCodeOptions.findIndex((ic) => ic._id === inviteCodeOption._id);
                  if (index !== -1) {
                    updatedInviteCodeOptions[index].customer = customerResponse.data.data.customer;
                    delete updatedInviteCodeOptions[index]._customerId;
                  }
                }
              } catch (error) {
                console.error("API error fetching customer for invite code:", inviteCodeOption._id, error);
              }
            })
          );
        }

        setInviteCodeOption(updatedInviteCodeOptions);
      }
    } catch (error) {
      console.error("API error:", error);
    }
    setLoading(false);
  };

  const triggerInviteCodeListUpdate = (inviteCode) => {
    console.log("New/Updated InviteCode: ", inviteCode);
    setShowInviteCodeForm(false);
    setLoading(false);
    fetchInviteCodes();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort invite codes by creation date (most recent first), then filter based on search term
  const sortedInviteCodes = [...inviteCodeOption].sort((a, b) => {
    const dateA = a.createdOnUTC ? new Date(a.createdOnUTC) : new Date(0);
    const dateB = b.createdOnUTC ? new Date(b.createdOnUTC) : new Date(0);
    return dateB - dateA; // Descending order (most recent first)
  });

  const filteredInviteCodes = sortedInviteCodes.filter((inviteCode) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const code = inviteCode.code?.toLowerCase() || "";
    const firstName = inviteCode.customer?.firstName?.toLowerCase() || "";
    const lastName = inviteCode.customer?.lastName?.toLowerCase() || "";
    const email = inviteCode.customer?.email?.toLowerCase() || "";
    return (
      code.includes(searchLower) ||
      firstName.includes(searchLower) ||
      lastName.includes(searchLower) ||
      email.includes(searchLower)
    );
  });

  // Paginate the filtered results
  const paginatedInviteCodes = filteredInviteCodes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="page inviteCode flex flex-column items-center p-4">
      {loading && <GlobalLoadingSpinner />}
      <div className="pageTitle flex flex-col w-full text-center lg:flex-row lg:text-left lg:justify-between mb-4">
        <h2>Invite Codes</h2>
        <div className="flex gap-2 items-center">
          <TextField
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0); // Reset to first page when searching
            }}
            sx={{ width: "300px" }}
            variant="outlined"
            size="small"
            placeholder="Search invite codes..."
          />
          <Button variant="contained" onClick={() => setShowInviteCodeForm(true)}>
            Add New Invite Code
          </Button>
        </div>
      </div>
      <div className="flex w-full">
        <div className="page-content min-h-h-screen flex flex-column justify-start w-full">
          <ModalPopup
            showModal={showInviteCodeForm}
            handleShowModal={() => setShowInviteCodeForm(false)}
            popupTitle="Add New Invite Code"
            popupContent={
              <InviteCodeForm
                callback={triggerInviteCodeListUpdate}
                cancelModal={() => {
                  setShowInviteCodeForm(false);
                }}
              />
            }
          />
          {inviteCodeOption.length === 0 && !loading ? (
            <Box sx={{ textAlign: "center", padding: "40px" }}>
              <p>No invite codes found.</p>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                width: "fit-content",
                maxWidth: "calc(100vw - 200px)",
                margin: "0 auto",
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: "8px"
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "4px"
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#555"
                  }
                }
              }}
            >
              <Table
                sx={{
                  minWidth: 650,
                  borderCollapse: "separate",
                  "td, th": { borderBottom: "1px solid rgba(224, 224, 224, 1)" }
                }}
                aria-label="invite codes table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>First Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Last Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Invite Code</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedInviteCodes.map((inviteCode) => (
                    <TableRow
                      key={inviteCode._id}
                      onClick={() => navigate(`/inviteCode/${inviteCode._id}`)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)"
                        }
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {inviteCode.customer?.firstName || "N/A"}
                      </TableCell>
                      <TableCell>{inviteCode.customer?.lastName || "N/A"}</TableCell>
                      <TableCell>{inviteCode.customer?.email || "N/A"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                        {inviteCode.code || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inviteCode.active ? "Active" : "Inactive"}
                          color={inviteCode.active ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredInviteCodes.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </TableContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteCode;
